import { Fragment, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { maskedAccount } from '../../../../shared/contracts/bank-account.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Button } from '../../../../shared/components/Button.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import type { BillGst, TdsBasis } from '../../../../shared/contracts/payment-taxes.js';
import { usePaymentTaxText } from './usePaymentTaxText.js';

/** GET /v1/payments/proposal/draft/:eligibilityId - every figure from records. */
interface VoucherDraft {
  readonly eligibilityId: string;
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly invoiceNumber: string;
  readonly grossAmount: number;
  readonly gst: BillGst;
  readonly tds: TdsBasis & {
    readonly panStatusCode: string;
    readonly base: number;
    readonly certificateBase: number;
  };
  readonly otherDeductions: number;
  readonly netAmount: number;
  readonly beneficiaryName: string;
  readonly beneficiaryAccountNo: string | null;
  readonly beneficiaryIfsc: string | null;
}

/**
 * L15.02 — New Voucher (owner, 30 Oct). The maker types nothing: the gross is
 * what the bill owes, the TDS is worked out from the tenant's TDS sections and
 * the vendor master, the other deductions are those recorded, and the
 * beneficiary is the vendor's bank on the vendor master. Raising the voucher
 * submits it to the tenant's payment approval workflow.
 */
export function NewVoucherPage(): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { money } = useBusinessNumber();
  const taxText = usePaymentTaxText();
  const { token } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const eligibilityId = params.get('eligibilityId') ?? '';

  const [draft, setDraft] = useState<VoucherDraft | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!eligibilityId) return;
    apiRequest<VoucherDraft>(`/v1/payments/proposal/draft/${encodeURIComponent(eligibilityId)}`, { token })
      .then((r) => { if (alive) setDraft(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [eligibilityId, token]);

  const handleCreate = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiRequest('/v1/payments/proposal', { token, method: 'POST', body: { eligibilityId } });
      setSubmitted(true);
    } catch (e) { setError((e as Error).message); }
    finally { setSubmitting(false); }
  }, [eligibilityId, token]);

  const row = (label: string, value: ReactNode, testId?: string, strong = false) => (
    <tr>
      <td style={{ padding: '12px 16px', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border-default)' }}>{label}</td>
      <td
        style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, fontSize: strong ? 18 : 14, borderBottom: '1px solid var(--color-border-default)' }}
        data-testid={testId}
      >
        {value}
      </td>
    </tr>
  );

  const tdsBasis = (d: VoucherDraft) => taxText.tdsBasis(d.tds, d.tds.base);

  return (
    <div className="tl-page" style={{ padding: 24, maxWidth: 760 }}>
      <PageHeader title={t('finance.voucher.title')} subtitle={t('finance.voucher.fromRecords')} />
      {!eligibilityId && <Alert tone="info">{t('finance.voucher.pickFromQueue')}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {submitted ? (
        <Card>
          <Alert tone="success">{t('finance.voucher.submitted')}</Alert>
          <div style={{ marginTop: 16 }}>
            <Button variant="secondary" onClick={() => navigate('/finance/maker/queue')}>{t('finance.voucher.backToQueue')}</Button>
          </div>
        </Card>
      ) : draft ? (
        <Card>
          <table aria-label="voucher" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              {row(t('finance.voucher.claim'), billRefLabel(draft.bill, day))}
              {row(t('finance.voucher.vendor'), personLabel(draft.vendorName, draft.vendorCode) ?? '—')}
              {row(t('finance.voucher.invoice'), draft.invoiceNumber)}
              {taxText.gstItems(draft.gst).map((g) => <Fragment key={g.label}>{row(g.label, g.value, g.testId)}</Fragment>)}
              {row(t('finance.voucher.gross'), money(draft.grossAmount), 'voucher-gross')}
              {row(t('finance.voucher.tds'), money(draft.tds.tdsAmount), 'voucher-tds')}
              {row(t('finance.voucher.tdsBasis'), tdsBasis(draft))}
              {row(t('finance.voucher.panStatus'), t(`finance.panStatus.${draft.tds.panStatusCode}`))}
              {row(t('finance.voucher.otherDeductions'), money(draft.otherDeductions))}
              {row(t('finance.voucher.net'), money(draft.netAmount), 'voucher-net', true)}
              {row(t('finance.voucher.beneficiary'), draft.beneficiaryName)}
              {row(t('finance.voucher.account'), draft.beneficiaryAccountNo ? maskedAccount(draft.beneficiaryAccountNo) : t('finance.voucher.noBank'))}
              {row(t('finance.voucher.ifsc'), draft.beneficiaryIfsc ?? t('finance.voucher.noBank'))}
            </tbody>
          </table>
          <p data-testid="voucher-gst-note" style={{ margin: '16px 0 0', color: 'var(--color-text-muted)' }}>{taxText.gstNote(draft.gst)}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <Button variant="ghost" onClick={() => navigate('/finance/maker/queue')}>{t('finance.voucher.backToQueue')}</Button>
            <Button variant="primary" onClick={() => void handleCreate()} disabled={submitting}>
              {submitting ? t('finance.voucher.creating') : t('finance.voucher.create')}
            </Button>
          </div>
        </Card>
      ) : eligibilityId && !error ? <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.voucher.loading')}</p> : null}
    </div>
  );
}
